import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Book,
  Terminal,
  Download,
  KeyRound,
  AlertTriangle,
  Copy,
  Check,
  FileCode,
  Boxes,
  Zap,
  Shield,
  Cpu
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Code block component with copy functionality
function CodeBlock({ code, language: _language = 'bash' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
        <code>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

// Section component for consistent styling
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-lg font-semibold">{title}</h4>
      {children}
    </div>
  );
}

export default function SDKDocumentation() {
  const { t } = useTranslation();

  const buildsBase = 'https://api.airgapsuite.com/api/public/downloads/builds';

  const agentInstall = `ARCH=$( [ "$(uname -m)" = "x86_64" ] && echo amd64 || echo arm64 )
curl -fSL -o agent.deb "${buildsBase}/airgapsuite-device-agent_latest_\${ARCH}.deb"
sudo dpkg -i agent.deb
systemctl status airgap-device-agent.service
ls -l /etc/pixuate/license.jwt /etc/pixuate/license.jwt.info.json`;

  const sdkInstall = `ARCH=$( [ "$(uname -m)" = "x86_64" ] && echo x86_64 || echo arm64 )
curl -fSL -o sdk.zip "${buildsBase}/airgapsuite-sdk_latest_\${ARCH}.zip"
sudo mkdir -p /opt/airgapsuite && sudo unzip -o sdk.zip -d /opt/airgapsuite

# airgapsuite-sdk-<arch>-<ver>/
#   lib/liblicense_sdk.so    C-ABI shared library (glibc >= 2.17)
#   include/license_sdk.h    C header (C and C++)
#   examples/example.cpp     reference usage`;

  const airgapPrepare = `# On a connected machine: pre-approve the serial in the portal (Devices), then fetch
BASE=${buildsBase}
curl -fSLO "$BASE/airgapsuite-sdk_latest_x86_64.zip"     # or _arm64.zip
curl -fSLO "$BASE/latest.json"
curl -fSL -o license-jwks.json https://api.airgapsuite.com/api/.well-known/license-jwks.json
# plus the signed license JWT for the box's serial (portal, or: airgap-license generate-license-file)
sha256sum airgapsuite-sdk_latest_*.zip                   # compare with latest.json
# copy everything to USB`;

  const airgapInstall = `# On the air-gapped box
sha256sum airgapsuite-sdk_latest_*.zip                   # re-check after the transfer
sudo mkdir -p /opt/airgapsuite /etc/pixuate
sudo unzip -o airgapsuite-sdk_latest_*.zip -d /opt/airgapsuite
sudo cp license.jwt /etc/pixuate/license.jwt
sudo cp license-jwks.json /etc/pixuate/license-jwks.json
airgap-license verify-jwt --file /etc/pixuate/license.jwt   # "License token is valid (verified offline)"`;

  const cExample = `#include "license_sdk.h"
#include <stdio.h>

int main(void) {
    if (sdk_init() != 0) {
        fprintf(stderr, "license init failed: %s\\n", sdk_get_last_error());
        return 1;
    }
    if (sdk_is_valid() != 1) {
        fprintf(stderr, "license invalid\\n");
        return 1;
    }
    long long cameras = sdk_get_num_cameras();      // -1 when the license sets no limit
    int footfall = sdk_verify_feature("footfall");  // 1 on, 0 off, <0 error
    printf("cameras=%lld footfall=%d\\n", cameras, footfall);
    return 0;
}`;

  const cBuild = `g++ -std=c++11 your_app.cpp \\
    -I/opt/airgapsuite/airgapsuite-sdk-<arch>-<ver>/include \\
    -L/opt/airgapsuite/airgapsuite-sdk-<arch>-<ver>/lib -llicense_sdk \\
    -Wl,-rpath,'$ORIGIN/lib' -o your_app`;

  const pythonExample = `import ctypes

lib = ctypes.CDLL("/opt/airgapsuite/.../lib/liblicense_sdk.so")
lib.sdk_init.restype = ctypes.c_int32
lib.sdk_is_valid.restype = ctypes.c_int32
lib.sdk_verify_feature.argtypes = [ctypes.c_char_p]
lib.sdk_verify_feature.restype = ctypes.c_int32
lib.sdk_get_num_cameras.restype = ctypes.c_int64

assert lib.sdk_init() == 0, "sdk_init failed"
if lib.sdk_is_valid() != 1:
    raise SystemExit("license invalid")

cameras = lib.sdk_get_num_cameras()
footfall = lib.sdk_verify_feature(b"footfall") == 1`;

  const nodeExample = `const koffi = require('koffi');
const lib = koffi.load('/opt/airgapsuite/.../lib/liblicense_sdk.so');

const sdk_init = lib.func('int32_t sdk_init()');
const sdk_is_valid = lib.func('int32_t sdk_is_valid()');
const sdk_get_num_cameras = lib.func('int64_t sdk_get_num_cameras()');
const sdk_verify_feature = lib.func('int32_t sdk_verify_feature(const char*)');

if (sdk_init() !== 0) throw new Error('sdk_init failed');
if (sdk_is_valid() !== 1) throw new Error('license invalid');
const cameras = sdk_get_num_cameras();`;

  const goExample = `/*
#cgo LDFLAGS: -L/opt/airgapsuite/.../lib -llicense_sdk -Wl,-rpath,/opt/airgapsuite/.../lib
#include "license_sdk.h"
*/
import "C"

func gate() error {
    if C.sdk_init() != 0 {
        return fmt.Errorf("sdk_init failed")
    }
    if C.sdk_is_valid() != 1 {
        return fmt.Errorf("license invalid")
    }
    return nil
}`;

  const encryptCmd = `# Build / CI step -- never on the edge device
export AIRGAP_PRODUCT_KEY=<product API key>   # env only; never a flag or a file
airgap-license encrypt --product <productId> --key detector-v3 model.onnx
# -> model.onnx.enc  (AGSF v1 envelope, AES-256-GCM). Ship this, not the plaintext.`;

  const decryptC = `uint8_t* buf = NULL;
size_t len = 0;
if (sdk_decrypt_file("detector-v3", "model.onnx.enc", &buf, &len) != 0) {
    fprintf(stderr, "license/key error: %s\\n", sdk_get_last_error());
    return 1;                                 // refuse to run
}
Ort::Session session(env, buf, len, opts);    // load from MEMORY, never write plaintext to disk
sdk_free_buffer(buf, len);`;

  const decryptPython = `buf = ctypes.POINTER(ctypes.c_ubyte)()
n = ctypes.c_size_t()
lib.sdk_decrypt_file.argtypes = [
    ctypes.c_char_p, ctypes.c_char_p,
    ctypes.POINTER(ctypes.POINTER(ctypes.c_ubyte)), ctypes.POINTER(ctypes.c_size_t),
]
assert lib.sdk_decrypt_file(b"detector-v3", b"model.onnx.enc",
                            ctypes.byref(buf), ctypes.byref(n)) == 0
data = ctypes.string_at(buf, n.value)
session = onnxruntime.InferenceSession(data)   # in-memory
lib.sdk_free_buffer(buf, n)`;

  const verifyCmd = `# Offline verification of the issued license (no network, no shared secret)
airgap-license verify-jwt --file /etc/pixuate/license.jwt
# -> "License token is valid (verified offline)"; also enforces hardware binding

# JWKS (public key) endpoint, cached by the CLI for air-gapped reuse
curl -fsSL https://api.airgapsuite.com/api/.well-known/license-jwks.json`;

  const floatingCmd = `airgap-license checkout  --key YOUR-LICENSE-KEY        # -> activation token
airgap-license heartbeat --token <activationToken>    # extend the lease
airgap-license release   --token <activationToken>    # free the seat`;

  const apiRows: [string, string, string][] = [
    ['int32_t sdk_init(void)', '0 ok, else error code', 'Call once at startup.'],
    ['const char* sdk_get_last_error(void)', 'message or NULL', 'Read after any failure.'],
    ['int32_t sdk_is_valid(void)', '1 valid / 0 invalid / <0 error', 'Gate the whole app.'],
    ['int32_t sdk_is_expired(void)', '1 / 0 / <0', 'Expiry check.'],
    ['int32_t sdk_verify_feature(const char* name)', '1 on / 0 off / <0 error', 'Feature or analytics gate.'],
    ['int64_t sdk_get_num_cameras(void)', 'count, or -1 if unset', 'Camera-count limit.'],
    ['FfiLicenseInfo* sdk_get_license(void)', 'struct pointer', 'Full details. Free with sdk_free_license_info.'],
    ['char* sdk_get_features(void)', 'JSON array string', 'List enabled analytics. Free with sdk_free_string.'],
    ['char* sdk_check_limit(const char* name)', 'JSON value string', 'Custom limits. Free with sdk_free_string.'],
    ['int32_t sdk_decrypt_file(name, path, &buf, &len)', '0 ok, <0 error', 'Decrypt a shipped .enc artifact.'],
    ['int32_t sdk_decrypt_buffer(name, in, in_len, &buf, &len)', '0 ok, <0 error', 'Same, from memory.'],
    ['int32_t sdk_get_secret(name, &buf, &len)', '0 ok, <0 error', 'Named secret (API key) in the clear.'],
    ['char* sdk_list_secret_names(void)', 'JSON array', 'Data keys carried by this license.'],
    ['void sdk_free_buffer(uint8_t*, size_t)', '-', 'Always free decrypt/secret output.'],
  ];

  const errorRows: [string, string, string][] = [
    ['-11', 'No data key with that name in this license', 'Create the key on the product in the portal, then wait one agent poll (5 min). Check sdk_list_secret_names().'],
    ['-13', 'Decrypt failed', 'Wrong key name or version for that file, or it was encrypted under a different product.'],
    ['-14', 'Bad envelope', 'Not an AGSF v1 file: plaintext shipped by mistake, or truncated / re-compressed in transit.'],
  ];

  const troubleRows: [string, string][] = [
    ['No /etc/pixuate/license.jwt', 'The device serial is not pre-approved yet. Provision it in the portal; the agent keeps polling.'],
    ['sdk_init != 0', 'Check sdk_get_last_error(). Make sure the license file exists and the agent has run.'],
    ['cannot open shared object file', 'liblicense_sdk.so is not on the loader path. Set an rpath, or copy it to /usr/lib and run ldconfig.'],
    ['sdk_get_num_cameras == -1', 'The license has no camera limit set. Fix the policy or license.'],
    ['dpkg: package architecture does not match', 'Wrong .deb. Use arm64 for Jetson/ARM and amd64 for Intel/AMD.'],
    ['sdk_is_valid == 0 on x86 with a hardware-serial error', 'The DMI product_uuid is root-only. Run the licensed process as root, or grant read on /sys/class/dmi/id/product_uuid.'],
    ['License invalid after copying to another machine', 'Working as intended: licenses are bound to the hardware serial. Request a transfer or a new license.'],
    ['Second identical unit is unlicensed', 'It has a different serial and needs its own pre-approved license.'],
  ];

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Workspace"
        icon={Book}
        title={t('sdkDocs.title', 'SDK Documentation')}
        subtitle={t('sdkDocs.subtitle', 'Learn how to integrate license verification into your application')}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-500" />
              {t('sdkDocs.quickStart', 'Quick Start')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">3 Steps</p>
            <p className="text-xs text-muted-foreground">agent, SDK, gate your app</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Boxes className="h-4 w-4 text-blue-500" />
              {t('sdkDocs.languages', 'Languages')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">Any (FFI)</p>
            <p className="text-xs text-muted-foreground">C/C++, Python, Node, Go, Rust, Java</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-500" />
              {t('sdkDocs.security', 'Security')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">Ed25519</p>
            <p className="text-xs text-muted-foreground">offline-verifiable, hardware-bound</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Cpu className="h-4 w-4 text-purple-500" />
              Platforms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">Linux</p>
            <p className="text-xs text-muted-foreground">arm64 (Jetson) and x86_64</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex w-full justify-start overflow-x-auto md:grid md:grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="install">Installation</TabsTrigger>
          <TabsTrigger value="api">API Reference</TabsTrigger>
          <TabsTrigger value="examples">Code Examples</TabsTrigger>
          <TabsTrigger value="encryption">Encrypted Files</TabsTrigger>
          <TabsTrigger value="troubleshooting">Troubleshooting</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('sdkDocs.overview.title', 'What is the License SDK?')}</CardTitle>
              <CardDescription>
                {t('sdkDocs.overview.description', 'A native library for verifying licenses in your application')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground">
                Two components work together on each device. The <strong>device agent</strong> claims the license
                pre-approved for the device&apos;s hardware serial and installs an Ed25519-signed license file. The{' '}
                <strong>SDK</strong> is a C-ABI shared library your program links to ask whether the license is valid,
                which features are on, how many cameras are licensed, and to decrypt model files shipped with it.
              </p>

              <Section title="How it works">
                <CodeBlock code={`portal (pre-approve serial)
   |
   v
device agent (.deb)  --claims-->  /etc/pixuate/license.jwt   (Ed25519 JWT, re-polled every 5 min)
                                          |
                                          v
your app  --links-->  liblicense_sdk.so   sdk_init() / sdk_is_valid() / sdk_verify_feature()`} />
              </Section>

              <Section title="Key properties">
                <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                  <li><strong>Offline verification:</strong> the license is an Ed25519 JWT verified against the issuer&apos;s public key (JWKS). No shared secret, works air-gapped.</li>
                  <li><strong>Hardware-bound:</strong> the license carries the device serial. Copying <code>license.jwt</code> to another machine fails validation.</li>
                  <li><strong>Revocation and renewal:</strong> the agent re-polls every 5 minutes, so suspensions and renewals reach the device without a reinstall.</li>
                  <li><strong>Encrypted artifacts:</strong> model weights and other files can be shipped encrypted and decrypted only on devices whose license carries the key.</li>
                </ul>
              </Section>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  Hardware serial source
                </div>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li><strong>Jetson / ARM:</strong> device-tree <code>serial-number</code>, readable by any user.</li>
                  <li><strong>x86 / amd64:</strong> SMBIOS <code>/sys/class/dmi/id/product_uuid</code>, which is <strong>root-only</strong>. The licensed process must run as root (or be granted read access), otherwise the SDK fails closed.</li>
                </ul>
              </div>

              <Section title="Offline verification and floating seats (CLI)">
                <CodeBlock code={verifyCmd} />
                <p className="text-sm text-muted-foreground">
                  For concurrent-seat licenses, the <code>airgap-license</code> client checks seats in and out:
                </p>
                <CodeBlock code={floatingCmd} />
              </Section>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="install" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Install the device agent and SDK
              </CardTitle>
              <CardDescription>
                Choose the architecture first: <code>uname -m</code> is <code>aarch64</code> for Jetson/ARM (arm64) and <code>x86_64</code> for Intel/AMD (amd64).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="1. Device agent">
                <CodeBlock code={agentInstall} />
                <p className="text-sm text-muted-foreground">
                  The agent reads the hardware serial, claims the pre-approved license, and writes{' '}
                  <code>/etc/pixuate/license.jwt</code> once the serial is approved. If the serial is not provisioned yet,
                  the file appears after an admin approves it; the agent keeps polling and needs no reinstall.
                </p>
              </Section>
              <Section title="2. SDK">
                <CodeBlock code={sdkInstall} />
                <p className="text-sm text-muted-foreground">
                  Ship <code>liblicense_sdk.so</code> next to your binary and link with{' '}
                  <code>-Wl,-rpath,&apos;$ORIGIN/lib&apos;</code>, or copy it to <code>/usr/lib</code> and run <code>ldconfig</code>.
                </p>
              </Section>
              <Section title="3. Verify checksums">
                <CodeBlock code={`curl -fsSL ${buildsBase}/latest.json   # version, sha256, size per artifact and arch
sha256sum agent.deb sdk.zip`} />
                <p className="text-sm text-muted-foreground">
                  The <code>_latest_</code> file names always point at the newest published build.
                </p>
              </Section>
              <Section title="4. Air-gapped box (no route to the portal)">
                <p className="text-sm text-muted-foreground">
                  The agent claims its license from the portal, so a box that never reaches it gets the license as a file.
                  Prepare the files on a connected machine:
                </p>
                <CodeBlock code={airgapPrepare} />
                <p className="text-sm text-muted-foreground">Then install from USB on the box:</p>
                <CodeBlock code={airgapInstall} />
                <p className="text-sm text-muted-foreground">
                  The SDK embeds the issuer public key, so <code>sdk_is_valid()</code> verifies the signature offline. The
                  hardware-serial binding still applies, and on x86 the licensed process must run as root. A renewal or
                  revocation only reaches the box when you bring in a new license file, or when it can poll the portal.
                </p>
              </Section>
              <p className="text-sm text-muted-foreground">
                Download links are on the <Link className="underline" to="/downloads">Downloads</Link> page.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="h-5 w-5" />
                C ABI reference
              </CardTitle>
              <CardDescription>
                All symbols are <code>extern &quot;C&quot;</code>. <code>license_sdk.h</code> is authoritative.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2 pr-4 font-medium">Function</th>
                      <th className="py-2 pr-4 font-medium">Returns</th>
                      <th className="py-2 font-medium">Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiRows.map(([fn, ret, use]) => (
                      <tr key={fn} className="border-b align-top">
                        <td className="py-2 pr-4"><code className="text-xs">{fn}</code></td>
                        <td className="py-2 pr-4 text-muted-foreground">{ret}</td>
                        <td className="py-2 text-muted-foreground">{use}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Section title="FfiLicenseInfo">
                <CodeBlock code={`FfiLicenseInfo {
    bool    is_valid;
    char*   license_key;
    char*   product_code;
    char*   product_name;
    char**  features;
    size_t  features_count;
    char*   expiration_date;
    char*   last_checked;
    char*   error_message;
    int64_t num_cameras;     // last, for ABI stability
}`} />
                <p className="text-sm text-muted-foreground">
                  Features may be a plain array or <code>{'{"analytics":[...],"numCameras":N}'}</code>; the SDK normalizes both.
                  Always free returned pointers with the matching <code>sdk_free_*</code> function.
                </p>
              </Section>
              <Section title="Enforcement pattern">
                <p className="text-sm text-muted-foreground">
                  Call <code>sdk_init()</code> then <code>sdk_is_valid()</code> at startup and refuse to run (or run degraded)
                  if the license is not valid. Gate optional capabilities with <code>sdk_verify_feature(&quot;name&quot;)</code> and cap
                  pipelines or streams at <code>sdk_get_num_cameras()</code>.
                </p>
              </Section>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="examples" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCode className="h-5 w-5" />
                Code examples
              </CardTitle>
              <CardDescription>
                The same shared library works from every language through FFI; no per-language rebuild.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="C / C++">
                <CodeBlock language="c" code={cExample} />
                <CodeBlock code={cBuild} />
              </Section>
              <Section title="Python (ctypes, no build step)">
                <CodeBlock language="python" code={pythonExample} />
              </Section>
              <Section title="Node.js (koffi)">
                <CodeBlock language="javascript" code={nodeExample} />
              </Section>
              <Section title="Go (cgo)">
                <CodeBlock language="go" code={goExample} />
              </Section>
              <Section title="Rust and Java">
                <p className="text-sm text-muted-foreground">
                  Rust: declare the <code>extern &quot;C&quot;</code> functions (or run <code>bindgen</code> on the header) and link{' '}
                  <code>-llicense_sdk</code>. Java: map an interface with JNA and load it with{' '}
                  <code>Native.load(&quot;license_sdk&quot;, LicenseSdk.class)</code>.
                </p>
              </Section>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="encryption" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                Shipping encrypted models and files
              </CardTitle>
              <CardDescription>
                Model weights (ONNX, PyTorch, TensorRT) and other files are encrypted at build time and decrypted only on licensed devices.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">
                A product has one or more <strong>named data keys</strong> (for example <code>detector-v3</code>, or a single{' '}
                <code>generic</code>). The key travels to the device inside the signed license, wrapped to that device&apos;s hardware
                serial. The SDK unwraps it and decrypts internally, so the raw key never crosses the FFI boundary. A copied
                encrypted file will not decrypt on a machine whose license lacks the key. The key name is the contract: use the
                same string at build time and at runtime.
              </p>
              <Section title="1. Create the key (once per product)">
                <p className="text-sm text-muted-foreground">
                  Portal, Products, row action <strong>Data keys</strong>, then <strong>Add key</strong>. The portal never shows key material.
                </p>
              </Section>
              <Section title="2. Encrypt at build time">
                <CodeBlock code={encryptCmd} />
              </Section>
              <Section title="3. Decrypt at runtime">
                <CodeBlock language="c" code={decryptC} />
                <CodeBlock language="python" code={decryptPython} />
              </Section>
              <Section title="Named secrets">
                <p className="text-sm text-muted-foreground">
                  For values your code must hold directly (an API key), use <code>sdk_get_secret(name, &amp;buf, &amp;len)</code>;{' '}
                  <code>sdk_list_secret_names()</code> returns the names in the license. Prefer <code>sdk_decrypt_file</code> for files.
                </p>
              </Section>
              <Section title="Rotation">
                <p className="text-sm text-muted-foreground">
                  Rotate in the portal. New encryptions use the new version; files already encrypted keep working because retired
                  versions stay in issued licenses. Devices need a re-issued license (next agent poll, within 5 minutes) before
                  they can decrypt new-version files.
                </p>
              </Section>
              <Section title="Error codes">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {errorRows.map(([code, meaning, fix]) => (
                        <tr key={code} className="border-b align-top">
                          <td className="py-2 pr-4"><Badge variant="outline">{code}</Badge></td>
                          <td className="py-2 pr-4 font-medium">{meaning}</td>
                          <td className="py-2 text-muted-foreground">{fix}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  Rules
                </div>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Run <code>airgap-license encrypt</code> in CI or packaging only, never on the edge device. It needs the product API key.</li>
                  <li>Never write decrypted bytes to disk. Load from the in-memory buffer.</li>
                  <li>Always call <code>sdk_free_buffer()</code> on decrypted output.</li>
                  <li>Gate startup on <code>sdk_is_valid()</code> first. Data-key calls on an invalid or foreign license fail by design.</li>
                  <li>Requires agent and SDK version 1.2.0 or later.</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="troubleshooting" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Troubleshooting</CardTitle>
              <CardDescription>Common symptoms and fixes.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {troubleRows.map(([symptom, fix]) => (
                      <tr key={symptom} className="border-b align-top">
                        <td className="py-2 pr-4 font-medium">{symptom}</td>
                        <td className="py-2 text-muted-foreground">{fix}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
